import { createSlice } from "@reduxjs/toolkit";
import API, { ENDPOINTS } from "../../services/api";
import { axiosInstance } from "../authentication/userSlices";
import { environment } from "../../environment";
import { getShops } from "./shopSlices";
export enum LocalStorageKeys {
  userToken = "userToken",
}

const initialState = {
  ic_products: [],
  op_products: [],
  ic_products_at_date: [],
  ic_products_consumption_top_5: [],
  ic_product_consumption_over_time: [],
  cumulative_ic_products: [],
  cumulative_op_products: [],
  ic_product_consumption: [],
  op_product_consumption: [],
  ic_product_consumption_per_product_top_5: [],
  bulk_loading: false,
  ic_forecast_boxes: {}
};

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setICProducts: (state, action) => {
      state.ic_products = action.payload;
    },
    setOPProducts: (state, action) => {
      state.op_products = action.payload;
    },
    setICProductsAtDate: (state, action) => {
      state.ic_products_at_date = action.payload;
    },
    setICProductsConsumptionInPeriod: (state, action) => {
      state.ic_products_consumption_top_5 = action.payload;
    },
    setICProductConsumptionOverTime: (state, action) => {
      state.ic_product_consumption_over_time = action.payload;
    },
    setCumulativeICProducts: (state, action) => {
      state.cumulative_ic_products = action.payload;
    },
    setCumulativeOPProducts: (state, action) => {
      state.cumulative_op_products = action.payload;
    },
    setICProductConsumption: (state, action) => {
      state.ic_product_consumption = action.payload;
    },
    setOPProductConsumption: (state, action) => {
      state.op_product_consumption = action.payload;
    },
    setICProductConsumptionPerProductTop5: (state, action) => {
      state.ic_product_consumption_per_product_top_5 = action.payload;
    },
    setBulkLoading: (state, action) => {
      state.bulk_loading = action.payload;
    },
    setICForecastBoxes: (state, action) => {
      state.ic_forecast_boxes = action.payload.distribution;
    }
  },
});

export const getICProductsByShop = (shopId: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopId}/ic_products`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setICProducts(formattedData));
    }
  } catch (error) {
    console.error(error);
  }
};

export const getICProductsByShopAtDate = (shopId: string, date: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopId}/ic_products/at_date?start_date=${date}`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setICProductsAtDate(formattedData));
    }
  } catch (error) {
    console.error(error);
  }
}

export const getICProductConsumptionInPeriod = (shopId: string, startDate: string, endDate: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopId}/ic_products/top_5_consumption_in_period/?start_date=${startDate}&end_date=${endDate}`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setICProductsConsumptionInPeriod(formattedData));
    }
  } catch (error) {
    console.error(error);
  }
}

export const getICProductConsumptionInPeriodPerProduct = (shopId: string, startDate: string, endDate: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopId}/ic_products/top_5_consumption_in_period/?start_date=${startDate}&end_date=${endDate}`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setICProductConsumptionPerProductTop5(formattedData));
    }
  } catch (error) {
    console.error(error);
  }
}
export const getICProductConsumptionOverTime = (shopId: string, startDate: string, endDate: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopId}/ic_products/consumption_in_period_per_day/?start_date=${startDate}&end_date=${endDate}`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setICProductConsumptionOverTime(formattedData));
      dispatch(setOPProductConsumption(null));
    }
  } catch (error) {
    console.error(error);
  }
}

export const getICProductConsumption = (shopIds: string[], startDate: string, endDate: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopIds.join(',')}/ic_products/consumption_in_period/?start_date=${startDate}&end_date=${endDate}`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setICProductConsumption(formattedData));
      dispatch(setOPProductConsumption(null));
    }
  } catch (error) {
    console.error(error);
  }
}
export const getCumulativeICProducts = (shopIds: string[], date: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/cumulative_ic_products?shop_ids=${shopIds.join(',')}&date=${date}`);
    if (res.status === 200) {
      const formattedData = res.data;
      dispatch(setCumulativeICProducts(formattedData));
      dispatch(setCumulativeOPProducts(null));
    }
  } catch (error) {
    console.error(error);
  }
}

export const getOPProductConsumption = (shopIds: string[], startDate: string, endDate: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopIds.join(',')}/op_products/consumption_in_period/?start_date=${startDate}&end_date=${endDate}`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setOPProductConsumption(formattedData));
      dispatch(setICProductConsumption(null));
    }
  } catch (error) {
    console.error(error);
  }
}

export const getCumulativeOPProducts = (shopIds: string[], date: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/cumulative_op_products?shop_ids=${shopIds.join(',')}&date=${date}`);
    if (res.status === 200) {
      const formattedData = res.data;
      dispatch(setCumulativeOPProducts(formattedData));
      dispatch(setCumulativeICProducts(null));
    }
  } catch (error) {
    console.error(error);
  }
}
export const updateICProductsByShop = (shopId: string, productId: string, product: any) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.put(`${environment.serverURL}/products/${shopId}/ic_products/${productId}`, product)
    if (res.status === 200) {
      dispatch(getICProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  }
};
export const updateICProductsByShopBulk = (shopId: string, products: any[]) => async (dispatch: Function) => {
  dispatch(setBulkLoading(true));
  try {
    const res = await axiosInstance.put(`${environment.serverURL}/products/${shopId}/ic_products/`, products)
    if (res.status === 200) {
      dispatch(getICProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(setBulkLoading(false));
  }
}
export const addICProductsByShop = (shopId: string, product: any) => async (dispatch: Function) => {
  try {

    const res = await axiosInstance.post(`${environment.serverURL}/products/${shopId}/ic_products/`, product)
    if (res.status === 200) {
      dispatch(getICProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  }
};
export const deleteICProductsByShop = ({shopId, productId}) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.delete(`${environment.serverURL}/products/${shopId}/ic_products/${productId}`)
    if (res.status === 200) {
      dispatch(getICProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  }
};

export const getOPProductsByShop = (shopId: string) => async (dispatch: Function) => {
  try {

    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopId}/op_products`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setOPProducts(formattedData));
    }
  } catch (error) {
    console.error(error);
  }
}
export const updateOPProductsByShop = (shopId: string, productId: string, product: any) => async (dispatch: Function) => {
  try {

    const res = await axiosInstance.put(`${environment.serverURL}/products/${shopId}/op_products/${productId}`, product)
    if (res.status === 200) {
      dispatch(getOPProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  }

}
export const addOPProductsByShop = (shopId: string, product: any) => async (dispatch: Function) => {
  try {

    const res = await axiosInstance.post(`${environment.serverURL}/products/${shopId}/op_products/`, product)
    if (res.status === 200) {
      dispatch(getOPProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  }
}
export const deleteOPProductsByShop = ({shopId, productId}) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.delete(`${environment.serverURL}/products/${shopId}/op_products/${productId}`)
    if (res.status === 200) {
      dispatch(getOPProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  }
}

export const updateOPProductsByShopBulk = (shopId: string, products: any[]) => async (dispatch: Function) => {
  console.log('products', products);
  dispatch(setBulkLoading(true));
  try {
    const res = await axiosInstance.put(`${environment.serverURL}/products/${shopId}/op_products/`, products)
    if (res.status === 200) {
      dispatch(getOPProductsByShop(shopId))
    }
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(setBulkLoading(false));
  }
}
export const getICForecastBoxes = (shopId: string, period: string) => async (dispatch: Function) => {
  try {
    const res = await axiosInstance.get(`${environment.serverURL}/products/${shopId}/forecast?period=${period}`)
    if (res.status === 200) {
      const formattedData = res.data
      dispatch(setICForecastBoxes(formattedData));
    }
  } catch (error) {
    console.error(error);
  }
}

export const {
  setICProducts,
  setOPProducts,
  setICProductsAtDate,
  setICProductsConsumptionInPeriod,
  setICProductConsumptionOverTime,
  setCumulativeICProducts,
  setCumulativeOPProducts,
  setICProductConsumption,
  setOPProductConsumption,
  setICProductConsumptionPerProductTop5,
  setBulkLoading,
  setICForecastBoxes
} = productSlice.actions;

export default productSlice.reducer;
